import mapKeys from 'lodash/mapKeys'

import { TOO_LONG_WAITING_TRANSACTION_STATUS } from 'state/actions/CMT/Notifications/base'
import delay from 'helpers/delay'

import {
    annotateMembersWithLogoutInfo,
    annotateMembersWithAccountStateInfo,
    getError,
    fetch,
    reflect,
    parseResult,
    processSpaResponse,
    combineStatistics,
    normalizeClan,
    snakeToCamel,
    normalizeStatisticsByDefaultValue,
} from './helpers'

async function getClanInfo(options, isCancelable) {
    try {
        const { gatewayHost, clanUrl } = options.urls

        const url = `${gatewayHost}${clanUrl}`
        const json = await fetch(url, {}, isCancelable)

        const clan = normalizeClan(json)
        if (!clan.members || !clan.members.length) {
            throw new Error('Unexpected behaviour: clan without members')
        }

        return clan
    } catch (e) {
        console.error(e) // eslint-disable-line no-console
        throw getError(e)
    }
}

const DEFAULT_STATISTICS_DATA = {
    battlesCount: 0,
    damagePerBattle: 0,
    expPerBattle: 0,
    lastBattleTime: 0,
    personalRating: 0,
    winsCount: 0,
    winsPercentage: 0,
}

/**
 * Basic method to fetch clan info from WGCG with WGRS and Exporter statistics.
 *
 * Requests are split into two groups: in the first request basic info
 * about clan and its members is fetched, the second group of requests
 * fetch info about ratings from WGRS, names from SPA, statistics from
 * Exporter. If the first request failed – method throws error.
 * If SPA in the second group of requests failed – method thorws error.
 * If requests for Exporter and WGRS failed – method thorws error.
 *
 * @param {object} options Method options.
 * @param {string|number} options.accountId ID of current account for check
 *             that this account in clan members
 * @param {string} options.battleType Selected battle type
 * @param {object} options.urls Object with paths to WGCG endpoints.
 * @param {string} options.urls.host WGCG host with 'http[s]://' prefix
 * @param {string} options.urls.clanUrl Path for clan info fetching
 * @param {string} options.urls.spaUrl Path for names info fetching
 * @param {string} [options.urls.exporterUrl] Path for statistics info fetching
 * @param {string} [options.urls.wgrsUrl] Path for ratings info fetching
 *
 * @returns {object} object with following schema:
 * {clan: {}, members: [Objects], statistics: {}}
 *
 * @example
 * import getClanInfoWithStatistics from 'proxy'
 * getClanInfoWithStatistics({options}).then((res) => {...})
 *
 */
export async function getClanInfoWithStatistics(options, isCancelable = false) {
    const {
        gatewayHost,
        spaUrl,
        exporterUrl,
        wgrsUrl,
        backendHost,
        wgshUrl,
    } = options.urls
    const { accountId, battleType } = options

    const clan = await getClanInfo(options, isCancelable)
    try {
        const membersIds = clan.members.map((member) => member.id)

        const options = { query: { account_ids: membersIds.join() } }
        const spaOptions = { query: { id: membersIds.join() } }

        let exporterFields = [
            'account_id', 'last_battle_time',
            'logout_at', 'global_rating',
        ]
        if (!wgrsUrl) {
            exporterFields = [
                ...exporterFields,
                `${battleType}.xp_amount`,
                `${battleType}.damage_dealt`,
                `${battleType}.wins_count`,
                `${battleType}.battles_count`,
            ]
        }

        const exporerOptions = { query: { account_ids: membersIds.join(), fields: exporterFields.join() } }
        const wgshOptions = {
            // from wgsh we need only suspended and anonymized info, so stats will be requested for 0 days
            query: { days: 0 },
        }

        const requests = [
            fetch(`${gatewayHost}${spaUrl}`, spaOptions, isCancelable),
            exporterUrl ?
                fetch(`${gatewayHost}${exporterUrl}`, exporerOptions, isCancelable) :
                Promise.resolve([]),
            wgrsUrl ?
                fetch(`${gatewayHost}${wgrsUrl}`, options, isCancelable) :
                Promise.resolve([]),
            fetch(`${backendHost}${wgshUrl}`, wgshOptions, isCancelable),
        ]

        const [
            spaResultRaw, exporterResultRaw, wgrsResultRaw, wgshResultRaw,
        ] = await Promise.all(requests.map(reflect))

        const spaResult = parseResult(spaResultRaw)
        const exporterResult = parseResult(exporterResultRaw, [])
        const wgrsResult = parseResult(wgrsResultRaw, [])

        let members = processSpaResponse(spaResult, clan.members, accountId)
        members = annotateMembersWithLogoutInfo(members, exporterResult)
        clan.members = undefined

        const statistics = combineStatistics(exporterResult, wgrsResult, battleType)

        const wgshResultArray = parseResult(wgshResultRaw)
        const accountStateInfo = {}
        wgshResultArray.forEach(memberStats => {
            accountStateInfo[memberStats.player_id] = {
                isSuspended: memberStats.isSuspended,
                isAnonymized: memberStats.isAnonymized,
            }
        })
        members = annotateMembersWithAccountStateInfo(members, accountStateInfo)

        normalizeStatisticsByDefaultValue(members, statistics, DEFAULT_STATISTICS_DATA)

        return { clan, members, statistics }

    } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
        throw getError(e)
    }
}

const DEFAULT_CLAN_MODES_STATISTICS = {
    sortiesMiddle: 0,
    sortiesChampion: 0,
    sortiesAbsolute: 0,
    sortiesMiddleWinsPercent: 0,
    sortiesChampionWinsPercent: 0,
    sortiesAbsoluteWinsPercent: 0,
    battles: 0,
    battlesWinsPercent: 0,
    industrialResourceEarnedAsLegionary: 0,
    industrialResourceEarnedAsClanmate: 0,
    industrialResourceWholeEarnedAsLegionary: 0,
    industrialResourceWholeEarnedAsClanmate: 0,

    industrialResourceEarnedInRandomInClanmatePlatoon: 0,
    industrialResourceWholeEarnedInRandomAsClanmatePlatoon: 0,
    industrialResourceEarnedInRandom: 0,
    industrialResourceWholeEarnedInRandom: 0,

    industrialResourceForClanPurchaseProduct: 0,

    industrialResourceClansupply: 0,

    industrialResource: 0,
    industrialResourceWhole: 0,
    playerId: undefined,
}

/**
 * Basic method to fetch clan info from WGCG with WGSH statistics.
 *
 * Requests are split into two groups: in the first request basic info
 * about clan and its members is fetched, the second group of requests
 * fetch info about statistics from WGRS and names from SPA.
 * If the first request failed – method throws error.
 * If SPA in the second group of requests failed – method thorws error.
 * If request for WGSH failed – method thorws error.
 *
 * @param {object} options Method options.
 * @param {string|number} options.accountId ID of current account for check
 *             that this account in clan members
 * @param {string} options.period Selected period
 * @param {object} options.urls Object with paths to WGCG endpoints.
 * @param {string} options.urls.host WGCG host with 'http[s]://' prefix
 * @param {string} options.urls.clanUrl Path for clan info fetching
 * @param {string} options.urls.spaUrl Path for names info fetching
 * @param {string} [options.urls.exporterUrl] Path for logout time info fetching
 * @param {string} [options.urls.wgshUrl] Path for statistics info fetching
 *
 * @returns {object} object with following schema:
 * {clan: {}, members: [Objects], statistics: {}}
 *
 * @example
 * import getClanInfoWithClanModesStatistics from 'proxy'
 * getClanInfoWithClanModesStatistics({...}).then((res) => {...})
 *
 */
export const getClanInfoWithClanModesStatistics = async (options, isCancelable = false) => {
    const { gatewayHost, backendHost, spaUrl, wgshUrl, exporterUrl } = options.urls
    const { period, accountId } = options

    try {
        const clan = await getClanInfo(options, isCancelable)

        const wgshOptions = {
            query: { days: period },
        }

        const membersIds = clan.members.map((member) => member.id)
        const spaOptions = {
            query: { id: membersIds.join() },
        }

        const exporterOptions = {
            query: { account_ids: membersIds.join(), fields: ['account_id', 'logout_at'].join() },
        }

        const requests = [
            fetch(`${gatewayHost}${spaUrl}`, spaOptions, isCancelable),
            fetch(`${backendHost}${wgshUrl}`, wgshOptions, isCancelable),
            fetch(`${gatewayHost}${exporterUrl}`, exporterOptions, isCancelable),
        ]

        const [
            spaResultRaw,
            wgshResultRaw,
            exporterResultRaw,
        ] = await Promise.all(requests.map(reflect))

        const spaResult = parseResult(spaResultRaw)
        let members = processSpaResponse(spaResult, clan.members, accountId)

        const exporterResult = parseResult(exporterResultRaw, [])
        members = annotateMembersWithLogoutInfo(members, exporterResult)
        clan.members = undefined

        const statisticsInArray = parseResult(wgshResultRaw)

        const statistics = {}
        statisticsInArray.forEach(memberStats => {
            statistics[memberStats.player_id] = mapKeys(memberStats, (value, key) => snakeToCamel(key) )
            statistics.playerId = undefined
        })

        members = annotateMembersWithAccountStateInfo(members, statistics)
        normalizeStatisticsByDefaultValue(members, statistics, DEFAULT_CLAN_MODES_STATISTICS)

        return { clan, members, statistics }

    } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
        throw getError(e)
    }
}

export const CHECK_TRANSACTION_FAIL_RETRY_IN_SEQUENCE = 5
const REJECT_AFTER_AWAIT_TRANSACTION_STATUS = 20000
const CHECK_TRANSACTION_WAIT_DELAY = 500

async function checkTransactionOfClanManagementOperation(
    host, transactionId, resolve, reject, startAt = Date.now(), retry = 1
){
    if (retry > CHECK_TRANSACTION_FAIL_RETRY_IN_SEQUENCE) {
        const error = new Error('Check status of transaction failed')
        reject(error)
        return
    }

    await delay(CHECK_TRANSACTION_WAIT_DELAY)
    try {
        const respose = await fetch(`${host}/clans/transactions/${transactionId}/`)

        const transactionStatus = respose.status
        switch (transactionStatus) {
        case 'committed': {
            resolve()
            break
        }
        case 'in_progress':
        case 'pending': {
            const awaitTime = Date.now() - startAt
            if (awaitTime > REJECT_AFTER_AWAIT_TRANSACTION_STATUS) {
                const error = new Error('Check status of transaction too long waiting')
                error.response = { code: TOO_LONG_WAITING_TRANSACTION_STATUS }
                reject(error)
            } else {
                checkTransactionOfClanManagementOperation(host, transactionId, resolve, reject, startAt)
            }
            break
        }
        default: {
            reject(new Error(`Transaction in status '${transactionStatus}'`))
        }
        }

    } catch (e) {
        console.error(e) // eslint-disable-line no-console
        checkTransactionOfClanManagementOperation(host, transactionId, resolve, reject, startAt, retry + 1)
    }
}

/**
 * Basic method to apply change clan operation, such as change role,
 * leader, remove members or separate treasure.
 *
 * Requests are split into two groups: in the first request basic info
 * about clan and its members is fetched, the second group of requests
 * fetch info about ratings from WGRS, names from SPA, statistics from
 * Exporter. If the first request failed – method throws error.
 * If SPA in the second group of requests failed – method thorws error.
 * If requests for Exporter and WGRS failed – method skips members
 * annotation with ratings info and statistics.
 *
 * @param {object} management Object with url and request type for method to
 *            apply. Should be one of the items of getManagementUrls() array
 * @param {object} options Method options.
 * @param {number|string} options.clanId ID of clan to which operation relates.
 * @param {string} options.host WGCG host with 'http[s]://' prefix
 * @param {object} options.data Data which will be send to the endpoint
 *
 * @returns {object} object with transaction id of performed operation
 * clan id and additional data:
 * { transaction_id: 12345, clan_id: 123, [account_ids : [1234, 4567]] }
 *
 *
 * @example
 * import { clanManagementOperation } from 'proxy'
 * clanManagementOperation({...}, {...}).then((res) => {...})
 */
export async function clanManagementOperation(management, options){
    const { url, method } = management
    const { clanId, host, data } = options

    try {
        let json = await fetch(`${host}${url.replace('%(clanId)s', clanId)}`, {
            method,
            body: JSON.stringify(data),
        })
        json = mapKeys(json, (value, key) => snakeToCamel(key))
        if (json.transactionId) {
            return new Promise((resolve, reject) => {
                checkTransactionOfClanManagementOperation(host, json.transactionId, () => {
                    resolve(json)
                }, reject)
            })
        }
        return json
    } catch (e) {
        console.error(e) // eslint-disable-line no-console
        throw getError(e)
    }
}



// WEBPACK FOOTER //
// ./containers/ClanManagement/proxy/index.js